import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Stethoscope } from "lucide-react";
import { api } from "../lib/api";
import { SERVICE_GROUPS, serviceGroupOf } from "../lib/domains";
import { ProductCard } from "../components/ProductCard";
import { Reveal, EASE } from "../components/Motion";

export default function Expertises() {
  const [products, setProducts] = useState([]);
  const [filter, setFilter] = useState("toutes");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products", { params: { category: "Prestations de service" } })
      .then(({ data }) => setProducts(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const visible = filter === "toutes" ? products : products.filter((p) => serviceGroupOf(p)?.key === filter);
  const grouped = SERVICE_GROUPS
    .map((g) => ({ ...g, items: visible.filter((p) => (serviceGroupOf(p)?.key || "autres") === g.key) }))
    .filter((g) => g.items.length > 0);

  return (
    <div data-testid="expertises-page">
      <section className="bg-navy py-20 text-white">
        <div className="mx-auto max-w-7xl px-6">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-sky-400/40 bg-sky-400/10 px-4 py-1.5 font-mono text-xs uppercase tracking-widest text-sky-300">
            <Stethoscope size={13} /> Cabinet CARIS Santé
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
            className="font-display text-4xl font-bold tracking-tight sm:text-5xl">
            Nos Expertises
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15, duration: 0.7, ease: EASE }}
            className="mt-5 max-w-3xl text-base leading-relaxed text-slate-300 md:text-lg" data-testid="expertises-intro">
            Cabinet CARIS Santé met à disposition son expertise pluridisciplinaire au service des établissements de santé,
            des institutions et des organisations en Côte d'Ivoire et en Afrique de l'Ouest.
          </motion.p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-14">
        <Reveal className="flex flex-wrap gap-2" data-testid="expertise-filters">
          <button
            data-testid="expertise-filter-toutes"
            onClick={() => setFilter("toutes")}
            className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-200 ${filter === "toutes" ? "bg-navy text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-ciel hover:text-ciel"}`}
          >
            Toutes les expertises
          </button>
          {SERVICE_GROUPS.map((g) => (
            <button
              key={g.key}
              data-testid={`expertise-filter-${g.key}`}
              onClick={() => setFilter(g.key)}
              className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors duration-200 ${filter === g.key ? `${g.badge}` : "border border-slate-200 bg-white text-slate-600 hover:border-ciel hover:text-ciel"}`}
            >
              {g.label}
            </button>
          ))}
        </Reveal>

        {loading && (
          <div className="flex justify-center py-20"><div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-ciel" /></div>
        )}

        {!loading && grouped.length === 0 && (
          <p className="py-20 text-center text-sm text-slate-500" data-testid="expertises-empty">
            Aucune prestation dans ce domaine pour le moment.
          </p>
        )}

        {grouped.map((g, gi) => (
          <div key={g.key} className="mt-14" data-testid={`expertise-group-${g.key}`}>
            <Reveal>
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wider ${g.badge}`}>{g.label}</span>
                <span className="font-mono text-xs text-slate-400">{g.items.length} prestation{g.items.length > 1 ? "s" : ""}</span>
              </div>
            </Reveal>
            <div className="mt-7 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {g.items.map((p) => <ProductCard key={p.product_id} product={p} />)}
            </div>
            {gi < grouped.length - 1 && <div className="mt-14 border-b border-slate-200" />}
          </div>
        ))}
      </section>
    </div>
  );
}
